import { flattenContactsToContactsSlider } from '@/modules/solutions/contact';
import {
  findNextItem,
  findPreviousItem,
} from '@/modules/core/js/array';
import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import size from 'lodash.size';
import React, { useState } from 'react';
import { Contact } from '@/modules/core/data/types/Contact';
import { ContactSlider } from '@/modules/core/data/types/ContactSlider';
import CircleBtn from '@/components/dataDisplay/CircleBtn';
import SolutionContactCard from './SolutionContactCard';

type Props = {
  contacts: Contact[];
  contactsMode?: 'minimal' | 'full';
};

/**
 * Slider to navigate between the information of several contacts.
 *
 * Converts the given "Contact[]" into "ContactSlider[]".
 * If a Contact is available in 2 places, it'll generate 2 ContactSlider entities.
 * Each ContactSlider will be displayed as-if it was a different contact.
 *
 * Has two display modes:
 *  - Minimal: Displays only minimal information, meant to be used within the SolutionCardTeaser.
 *    - Only the more relevant information are shown (name, title, email, tel, place name).
 *  - Full: Displays all information, meant to be used within the SolutionCardDetails.
 *    - More information are shown (place address, place description, place building room info).
 *
 * @param props
 */
const ContactsSlider: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    contacts,
    contactsMode = 'minimal',
  } = props;
  const contactsSlider: ContactSlider[] = flattenContactsToContactsSlider(contacts);
  const [contactSlider, setContactSlider] = useState<ContactSlider>(contactsSlider?.[0]);
  const contactsSliderSize = size(contactsSlider);

  if (contactsSliderSize === 0) {
    return null;
  }

  return (
    <div
      className={'contacts-slider'}
      css={css`
        margin: 10px;
        display: inline-flex;
        justify-content: space-between;
        align-items: center;
      `}
    >
      {
        contactsSliderSize > 1 && (
          <CircleBtn
            mode={'primary-outline'}
            margin={'10px'}
            onClick={() => {
              const previous: ContactSlider = findPreviousItem(contactSlider, contactsSlider);
              setContactSlider(previous);
            }}
          >
            <FontAwesomeIcon icon="arrow-left" />
          </CircleBtn>
        )
      }

      <SolutionContactCard
        contact={contactSlider}
        mode={contactsMode}
      />

      {
        contactsSliderSize > 1 && (
          <CircleBtn
            mode={'primary-outline'}
            margin={'10px'}
            onClick={() => {
              const next: ContactSlider = findNextItem(contactSlider, contactsSlider);
              setContactSlider(next);
            }}
          >
            <FontAwesomeIcon icon="arrow-right" />
          </CircleBtn>
        )
      }
    </div>
  );
};

export default ContactsSlider;
